import React from 'react'
import {
  Button,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableHead,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { useIntl } from 'react-intl'
import { colorPalette } from '../../../../style/colorPalette'
import { SprintAdjustmentItem } from './types'
import { TableHeaderCell, TableDataCell } from './StyledComponents'
import { NumberTextInput } from '../../../../components/inputs/NumberTextInput'

interface Props {
  linkedItems: SprintAdjustmentItem[]
  unlinkedItems: SprintAdjustmentItem[]
  workloadUnitLabel: string
  addMenuAnchor: HTMLElement | null
  onAddMenuOpen: (event: React.MouseEvent<HTMLButtonElement>) => void
  onAddMenuClose: () => void
  onAddSprint: (sprintUuid: string) => void
  onRemoveSprint: (sprintUuid: string) => void
  onWorkloadChange: (sprintUuid: string, value: string) => void
  onSwitchToSelection: () => void
  formatDate: (dateStr: string) => string
  convertWorkloadFromHourToSelectedUnit: (hour: number) => number
}

export const WorkloadModeView: React.FC<Props> = ({
  linkedItems,
  unlinkedItems,
  workloadUnitLabel,
  addMenuAnchor,
  onAddMenuOpen,
  onAddMenuClose,
  onAddSprint,
  onRemoveSprint,
  onWorkloadChange,
  onSwitchToSelection,
  formatDate,
  convertWorkloadFromHourToSelectedUnit,
}) => {
  const intl = useIntl()

  return (
    <>
      {/* アクションバー */}
      <Box
        sx={{
          px: 3,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          startIcon={<AddIcon />}
          onClick={onAddMenuOpen}
          disabled={unlinkedItems.length === 0}
          size="small"
        >
          {intl.formatMessage({
            id: 'productBacklog.sprintInteractiveAdjustment.addSprint',
          })}
        </Button>
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={onAddMenuClose}
        >
          {unlinkedItems.map(item => (
            <MenuItem
              key={item.sprintUuid}
              onClick={() => onAddSprint(item.sprintUuid)}
            >
              {item.sprintName}（{formatDate(item.startDate)}～
              {formatDate(item.endDate)}）
            </MenuItem>
          ))}
        </Menu>
        <Button size="small" onClick={onSwitchToSelection}>
          {intl.formatMessage({
            id: 'productBacklog.sprintInteractiveAdjustment.editSelection',
          })}
        </Button>
      </Box>
      {/* スプリント一覧テーブル */}
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader={true}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>
                {intl.formatMessage({
                  id: 'productBacklog.sprintInteractiveAdjustment.sprint',
                })}
              </TableHeaderCell>
              <TableHeaderCell align="right">
                {intl.formatMessage({
                  id: 'productBacklog.sprintInteractiveAdjustment.estimatedHour',
                })}
                ({workloadUnitLabel})
              </TableHeaderCell>
              <TableHeaderCell align="center" sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {linkedItems.length === 0 ? (
              <TableRow>
                <TableDataCell colSpan={3} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {intl.formatMessage({
                      id: 'productBacklog.sprintInteractiveAdjustment.noSprints',
                    })}
                  </Typography>
                </TableDataCell>
              </TableRow>
            ) : (
              linkedItems.map(item => (
                <TableRow key={item.sprintUuid} hover={true}>
                  <TableDataCell>
                    {item.sprintName}（{formatDate(item.startDate)}～
                    {formatDate(item.endDate)}）
                  </TableDataCell>
                  <TableDataCell align="right">
                    <NumberTextInput
                      value={convertWorkloadFromHourToSelectedUnit(
                        item.estimatedHour
                      )}
                      onChange={e =>
                        onWorkloadChange(item.sprintUuid, e.target.value)
                      }
                      inputProps={{
                        min: 0,
                        step: 1,
                        onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                          e.target.select(),
                      }}
                      sx={{
                        width: 100,
                        '& > input': {
                          textAlign: 'right',
                          padding: '6px 8px',
                          border: `1px solid ${colorPalette.monotone[2]}`,
                          borderRadius: '4px',
                          '&:focus': {
                            borderColor: colorPalette.skyBlue[7],
                            outline: 'none',
                          },
                        },
                      }}
                    />
                  </TableDataCell>
                  <TableDataCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveSprint(item.sprintUuid)}
                      sx={{ color: colorPalette.monotone[4] }}
                      tabIndex={-1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableDataCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}
